<template>
    <div class="tw:flex tw:flex-col tw:gap-2">
        <p v-if="label" class="tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:m-0">
            {{ label }}
        </p>

        <div v-if="loading" class="tw:flex tw:flex-col tw:gap-2" role="status" :aria-label="$t('seat_picker.loading')">
            <Skeleton
                v-for="n in skeletonCount"
                :key="n"
                padding="tw:p-0"
                custom-class="tw:shadow-none tw:border tw:border-gray-100"
            >
                <template #skeleton>
                    <SkeletonItem height="tw:h-14" />
                </template>
            </Skeleton>
        </div>

        <p v-else-if="!zones.length" class="tw:text-sm tw:text-gray-400 tw:text-center tw:py-4 tw:m-0">
            {{ emptyText || $t('seat_picker.empty') }}
        </p>

        <ul v-else class="tw:list-none tw:m-0 tw:p-0 tw:flex tw:flex-col tw:gap-3" role="listbox" :aria-label="label">
            <li
                v-for="zone in zones"
                :key="zone.id"
                role="option"
                :aria-disabled="!zone.available"
                class="tw:rounded-lg"
                :style="highlightWrapperStyle(zone)"
                @mouseenter="zone.available ? $emit('zone-hover', zone.id, true) : null"
                @mouseleave="zone.available ? $emit('zone-hover', zone.id, false) : null"
                @click="zone.available ? handleSelect(zone) : null"
            >
                <CardFront
                    :custom-classes="[
                        'tw:px-2 tw:py-3 tw:cursor-pointer tw:select-none tw:overflow-hidden',
                        itemClasses(zone),
                    ].join(' ')"
                >
                    <div class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:w-full">
                        <div class="tw:flex tw:items-center tw:gap-3 tw:min-w-0">
                            <span
                                class="tw:w-2.5 tw:h-5 tw:rounded-full tw:flex-shrink-0"
                                :style="zoneDotColor(zone) ? { backgroundColor: zoneDotColor(zone) } : {}"
                                :class="!zoneDotColor(zone) ? 'tw:bg-gray-400' : ''"
                                aria-hidden="true"
                            ></span>
                            <div class="tw:flex tw:flex-col tw:min-w-0">
                                <span class="tw:text-sm tw:font-semibold tw:truncate tw:leading-tight">
                                    {{ zone.name }}
                                </span>
                                <span
                                    v-if="zone.auxLabel"
                                    class="tw:text-xs tw:text-gray-500 tw:truncate tw:leading-tight"
                                >
                                    {{ zone.auxLabel }}
                                </span>
                            </div>
                        </div>
                        <div class="tw:flex tw:items-center tw:gap-2 tw:flex-shrink-0">
                            <span
                                v-if="zone.price != null"
                                class="tw:text-sm tw:font-bold"
                                :class="zone.available ? 'tw:text-gray-700' : 'tw:text-gray-400'"
                            >
                                {{ formatPrice(zone.price) }}
                            </span>
                            <BadgeFront
                                v-if="!zone.available"
                                :label="$t('seat_picker.unavailable')"
                                variant="danger"
                                :dot="true"
                            />
                            <BadgeFront
                                v-else-if="showAvailability && zone.availability != null"
                                :label="String(zone.availability)"
                                variant="success"
                                :dot="true"
                            />
                        </div>
                    </div>
                </CardFront>
            </li>
        </ul>
    </div>
</template>

<script setup>
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import Skeleton from '@/modules/front/ui/skeleton/Skeleton.vue';
    import SkeletonItem from '@/modules/front/ui/skeleton/SkeletonItem.vue';
    import { useFilters } from '@/composables/front/formatters/useFilters';

    const props = defineProps({
        /** Array of zone objects to render */
        zones: {
            type: Array,
            default: () => [],
        },
        /** Whether the list is in a loading state */
        loading: {
            type: Boolean,
            default: false,
        },
        /** Number of skeleton rows to show while loading */
        skeletonCount: {
            type: Number,
            default: 3,
        },
        /** Section label rendered above the list */
        label: {
            type: String,
            default: null,
        },
        /** Custom empty-state message (overrides i18n default) */
        emptyText: {
            type: String,
            default: null,
        },
        /** Whether to show the availability count badge on available zones */
        showAvailability: {
            type: Boolean,
            default: false,
        },
        /**
         * Real SVG fill colours per zone id, emitted by VenueMap via 'zone-colors'.
         * Used as a secondary fallback when zone.color (API field) is absent.
         * Shape: { [zoneId: string]: string }
         */
        zoneColors: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Zone id currently highlighted from an external source (e.g. user hovers the SVG map).
         * The matching row gets background-color var(--alttextcolor) and border var(--altcolor)
         * via highlightWrapperStyle — identical to the legacy `highlighted-table` CSS rule.
         */
        hoveredZoneId: {
            type: [String, Number],
            default: null,
        },
    });

    const emit = defineEmits([
        /**
         * Emitted when the user clicks an available zone row.
         * @payload {Object} zone — the full zone object from the `zones` prop
         */
        'select',
        /**
         * Emitted on mouseenter/mouseleave of an available zone row.
         * @payload {number|string} zoneId
         * @payload {boolean}       entering — true = mouseenter, false = mouseleave
         */
        'zone-hover',
    ]);

    function handleSelect(zone) {
        emit('select', zone);
    }

    const { formatearPrecio } = useFilters();

    function formatPrice(value) {
        if (value == null) return '';
        return formatearPrecio(value);
    }

    /**
     * Resolves the dot colour for a zone row.
     * Priority:
     *   1. zone.color  — direct API field, same source as legacy TablaSectoresComponent.
     *   2. zoneColors[zone.id] — extracted from the SVG fill after map attach (optional, slower).
     *   3. null — fallback to the tw:bg-gray-400 CSS class.
     */
    function zoneDotColor(zone) {
        return zone.color || props.zoneColors[String(zone.id)] || null;
    }

    // eslint-disable-next-line eqeqeq
    function isExternalHover(zone) {
        return props.hoveredZoneId != null && zone.id == props.hoveredZoneId;
    }

    function itemClasses(zone) {
        if (!zone.available) {
            return 'tw:bg-gray-50 tw:border-gray-200 tw:text-gray-400 tw:cursor-not-allowed tw:opacity-70';
        }
        return ' tw:text-gray-800 tw:hover:bg-gray-50 tw:hover:border-gray-300';
    }

    /**
     * Returns an inline style OBJECT for the <li> wrapper to highlight the row
     * when hoveredZoneId matches this zone (map → list direction).
     *
     * Replicates the legacy `highlighted-table` CSS rule exactly:
     *   background-color: var(--alttextcolor)
     *   color:            var(--altcolor)
     *   border:           1.5px solid var(--altcolor)
     *   font-weight:      bold
     *
     * Using CSS vars directly means the highlight always works regardless of
     * whether zoneColors has been populated — same guarantee as the legacy code.
     */
    function highlightWrapperStyle(zone) {
        if (!isExternalHover(zone)) return {};
        return {
            backgroundColor: 'var(--alttextcolor)',
            color: 'var(--altcolor)',
            border: '1.5px solid var(--altcolor)',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
        };
    }
</script>

